import EditIcon from "@mui/icons-material/Edit";
import { Tooltip } from "@mui/material";
import { FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { CrudColumnDef } from "@/components/tables/crudColumnDef";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import Modal from "@/components/modal";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import FileUploadInput from "@/components/forms/inputs/upload";
import { apiRequest } from "@/helper/api";
import type { FileUploadRequestOptions } from "@/helper/customHooks/useFileUploadProgress";
import MissingTranslationsImportResult, {
  MissingTranslationsImportResultData,
} from "@/components/forms/translation/missingTranslationsImportResult";

interface MissingTranslationRow {
  entity: string;
  object_id: string;
  field: string;
  field_key: string | null;
  de: string;
  en: string | null;
  single_editable: boolean;
}

const MissingTranslationsTable: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showConfirmationModal } = useConfirmationModal();
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importInProgress, setImportInProgress] = useState(false);
  const [refreshTable, setRefreshTable] = useState(false);
  const exportParamsRef = useRef<{
    globalFilter?: string;
    additionalUrlParameters?: Record<string, unknown>;
    ordering?: string;
  }>({});

  usePageHeading(t("missing_translations"));

  const columns: CrudColumnDef[] = [
    { header: t("entity"), accessorKey: "entity", id: "entity" },
    { header: t("object_id"), accessorKey: "object_id", id: "object_id" },
    { header: t("field"), accessorKey: "field", id: "field" },
    { header: t("field_key"), accessorKey: "field_key", id: "field_key" },
    {
      header: t("language_german"),
      accessorKey: "de",
      id: "de",
    },
    {
      header: t("language_english"),
      accessorKey: "en",
      id: "en",
    },
  ];

  const handleImport = async (
    files: File[],
    { onUploadProgress }: FileUploadRequestOptions
  ) => {
    const file = files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const response = await apiRequest<MissingTranslationsImportResultData>({
      entity: "translation",
      additionalRouteParts: { import_missing: "/" },
      method: "post",
      submitData: formData,
      shouldNotImpersonate: true,
      disableGlobalSpinner: true,
      config: {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
      },
    });
    setImportModalOpen(false);
    if (response.data.accepted) setRefreshTable((current) => !current);
    showConfirmationModal(
      <MissingTranslationsImportResult
        fileName={file.name}
        result={response.data}
      />,
      {
        title: t("missing_translations_import_completed"),
        disableCancelButton: true,
      }
    );
  };

  const topToolbarActions = () => (
    <>
      <GenericButton onClick={() => setImportModalOpen(true)}>
        {t("missing_translations_import")}
      </GenericButton>
      <GenericButton
        onClick={() => {
          const queryParameters = new URLSearchParams({ file_format: "csv" });
          Object.entries(
            exportParamsRef.current.additionalUrlParameters ?? {}
          ).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              queryParameters.set(key, String(value));
            }
          });
          if (exportParamsRef.current.ordering) {
            queryParameters.set("ordering", exportParamsRef.current.ordering);
          }
          if (exportParamsRef.current.globalFilter?.trim()) {
            queryParameters.set(
              "search",
              exportParamsRef.current.globalFilter.trim()
            );
          }
          window.location.href = `/api/v1/translation/export_missing/?${queryParameters}`;
        }}
      >
        {t("csv_export")}
      </GenericButton>
    </>
  );

  return (
    <>
      <GenericCrudTable
        entity="translation"
        columns={columns}
        additionalRoutePartsForGet={{ missing: "/" }}
        additionalCustomTopToolbarElements={topToolbarActions}
        onExportParamsChange={(params) => {
          exportParamsRef.current = params;
        }}
        allowColumnFiltering={true}
        disableAddItem
        disallowEdit
        disallowDelete
        hideEditButtonForRows={true}
        hideDeleteButtonForRows={true}
        showRowActions
        refreshTable={refreshTable}
        customRowActions={(row: { original: MissingTranslationRow }) => {
          const missingTranslation = row.original;
          const disabled = !missingTranslation.single_editable;
          return (
            <Tooltip
              title={
                disabled ? t("missing_translations_import_required") : t("edit")
              }
            >
              <span>
                <GenericButton
                  variant="icon"
                  color="iconBase"
                  disabled={disabled}
                  onClick={() =>
                    navigate(
                      `/${missingTranslation.entity}/${missingTranslation.object_id}/translation?returnTo=/settings/missingTranslations`
                    )
                  }
                >
                  <EditIcon />
                </GenericButton>
              </span>
            </Tooltip>
          );
        }}
      />
      <Modal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        title={t("missing_translations_import")}
        hideDialogActions
        disableCancelButton={importInProgress}
        maxWidth="40rem"
      >
        <FileUploadInput
          multiple={false}
          onCancel={() => setImportModalOpen(false)}
          customUploadHandler={handleImport}
          accept={[".csv", ".xlsx"]}
          onUploadStateChange={setImportInProgress}
        />
      </Modal>
    </>
  );
};

export default MissingTranslationsTable;
